/**
 * OverviewPage.tsx — /app/overview — errors + RUM summary
 *
 * Purpose:    Errors panel (events vs quota, top groups) and RUM panel
 *             (pageviews vs quota, p75 LCP/INP, error rate).
 * Inputs:     overviewApi + billingApi from @/lib/saas-client
 * Outputs:    Overview dashboard UI
 * Constraints: Dark-first styling; WCAG AA
 * SOT:        nsentry SaaS app zone
 */
import { useEffect, useState } from 'react'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { overviewApi, billingApi, type Overview } from '@/lib/saas-client'
import { TIERS, usagePct, type TierId } from '@/lib/tiers'
import type { SentryError } from '@/lib/sentry-error'

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-brand-surface-2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div
        className={`h-full rounded-full ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-sky-500'}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

export function OverviewPage() {
  const [state, setState] = useState<AsyncState>('loading')
  const [overview, setOverview] = useState<Overview | null>(null)
  const [tier, setTier] = useState<TierId>('free')
  const [error, setError] = useState<SentryError | undefined>()

  async function load() {
    setState('loading')
    const [overviewRes, billingRes] = await Promise.all([overviewApi.get(), billingApi.get()])
    if (!overviewRes.ok) {
      setError(overviewRes.error)
      setState('error')
      return
    }
    setOverview(overviewRes.value)
    if (billingRes.ok) setTier(billingRes.value.tier)
    setState('success')
  }

  useEffect(() => {
    void load()
  }, [])

  const quota = TIERS[tier]

  return (
    <div className="p-6 lg:p-8">
      <h1 className="mb-6 text-xl font-semibold text-brand-ink">Overview</h1>

      <AsyncScreen state={state} error={error} onRetry={load}>
        {overview && (
          <div className="grid gap-6 lg:grid-cols-2">
            <section className="rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Uptime monitoring">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-brand-muted">Monitors</h2>
              <dl className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <dt className="text-xs text-brand-muted">Up</dt>
                  <dd className="mt-1 text-2xl font-bold text-green-700 dark:text-green-400">{overview.monitors.up}</dd>
                </div>
                <div>
                  <dt className="text-xs text-brand-muted">Down</dt>
                  <dd className={`mt-1 text-2xl font-bold ${overview.monitors.down > 0 ? 'text-red-700 dark:text-red-400' : 'text-brand-ink'}`}>{overview.monitors.down}</dd>
                </div>
                <div>
                  <dt className="text-xs text-brand-muted">Paused</dt>
                  <dd className="mt-1 text-2xl font-bold text-brand-muted">{overview.monitors.paused}</dd>
                </div>
                <div>
                  <dt className="text-xs text-brand-muted">Pending</dt>
                  <dd className="mt-1 text-2xl font-bold text-brand-muted">{overview.monitors.pending}</dd>
                </div>
              </dl>
              <p className="mt-4 text-sm text-brand-muted">
                {overview.monitors.total} monitor{overview.monitors.total === 1 ? '' : 's'} ·{' '}
                {overview.uptimePct24h === null
                  ? 'no probes recorded yet'
                  : `${overview.uptimePct24h}% recent uptime`}
              </p>
            </section>

            <section className="rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Incidents">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-brand-muted">Incidents</h2>
              <div className="flex items-baseline gap-3">
                <span className={`text-2xl font-bold ${overview.incidentsOpen > 0 ? 'text-red-700 dark:text-red-400' : 'text-brand-ink'}`}>
                  {overview.incidentsOpen}
                </span>
                <span className="text-sm text-brand-muted">open incident{overview.incidentsOpen === 1 ? '' : 's'}</span>
              </div>
              <p className="mt-4 text-sm text-brand-muted">
                {overview.incidentsOpen === 0 ? 'All clear — nothing needs attention.' : 'Review and acknowledge on the Incidents screen.'}
              </p>
            </section>

            <section className="rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Errors">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-brand-muted">Errors</h2>
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-2xl font-bold text-brand-ink">{overview.errors.eventsThisMonth.toLocaleString()}</span>
                <span className="text-xs text-brand-muted">/ {quota.errorEventsPerMonth.toLocaleString()} events this month</span>
              </div>
              <ProgressBar pct={usagePct(overview.errors.eventsThisMonth, quota.errorEventsPerMonth)} />

              <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-brand-muted">Top error groups</h3>
              <ul className="space-y-2">
                {overview.errors.topGroups.map((g, i) => (
                  <li key={i} className="flex items-center justify-between rounded-md bg-brand-bg px-3 py-2 text-sm">
                    <span className="truncate text-gray-200" title={g.message}>{g.message}</span>
                    <span className="ml-3 flex-shrink-0 text-xs text-brand-muted">{g.count}×</span>
                  </li>
                ))}
                {overview.errors.topGroups.length === 0 && (
                  <li className="text-sm text-brand-muted">No errors recorded</li>
                )}
              </ul>
            </section>

            <section className="rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Real user monitoring">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-brand-muted">RUM</h2>
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-2xl font-bold text-brand-ink">{overview.rum.pageviewsThisMonth.toLocaleString()}</span>
                <span className="text-xs text-brand-muted">/ {quota.rumPageviewsPerMonth.toLocaleString()} pageviews this month</span>
              </div>
              <ProgressBar pct={usagePct(overview.rum.pageviewsThisMonth, quota.rumPageviewsPerMonth)} />

              <dl className="mt-6 grid grid-cols-3 gap-4 text-center">
                <div>
                  <dt className="text-xs text-brand-muted">p75 LCP</dt>
                  <dd className="mt-1 text-lg font-semibold text-brand-ink">{overview.rum.p75LcpMs}ms</dd>
                </div>
                <div>
                  <dt className="text-xs text-brand-muted">p75 INP</dt>
                  <dd className="mt-1 text-lg font-semibold text-brand-ink">{overview.rum.p75InpMs}ms</dd>
                </div>
                <div>
                  <dt className="text-xs text-brand-muted">Error rate</dt>
                  <dd className="mt-1 text-lg font-semibold text-brand-ink">{overview.rum.errorRatePct}%</dd>
                </div>
              </dl>
            </section>
          </div>
        )}
      </AsyncScreen>
    </div>
  )
}
